/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { BaseGuildView } from './base-guild-view.js';
import { guildViewStyles } from './guild-view-styles.js';
import { TabRouteMixin } from '../../modules/tab-route-mixin.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/menu-label/menu-label.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-alert-bar/q1-alert-bar.js';
import '../../components/q1-guild-activation-panel/q1-guild-activation-panel.js';
import '../../components/q1-log-item-card/q1-log-item-card.js';
import '../../components/q1-markdown/q1-markdown.js';
import '../../components/q1-member-chip/q1-member-chip.js';
import '../../components/q1-membership-list/q1-membership-list.js';
import '../../components/q1-projects-list/q1-projects-list.js';
import '../../components/q1-spinner-panel/q1-spinner-panel.js';

/*=== Q1 MODULES ===*/
import { buildRefPathMap } from '../../modules/cache-helpers-client.js';
import { enrichLogItem } from '../../modules/log-item-helpers-client.js';
import { enrichMember } from '../../modules/member-helpers-client.js';
import { enrichMembership } from '../../modules/membership-helpers-client.js';
import { enrichProject } from '../../modules/project-helpers-client.js';
import { guildDetailViewCaches } from '../../models/guild-client-settings.js';
import { 
  q1LogItemFunctions, 
  q1VersionFunctions, 
  q1ExternalResourceFunctions 
} from '../../modules/q1-core-functions.js';
import { guildActivationStatusMap } from '../../models/guild-client-enums.js';
import { externalResourceIconMap } from '../../models/external-resource-client-enums.js';

/*=== CONSTANTS ===*/

const CACHE_MAX_AGE = 10; // in minutes

const LOG_ITEMS_PAGE_SIZE = 20;

const TAB_SETTINGS = (guild) => ({
  home: { path: `/${guild.key}`, title: `${guild.name} - q1 Network` },
  about: { path: `/${guild.key}/about`, title: `About: ${guild.name} - q1 Network` },
  members: { path: `/${guild.key}/members`, title: `Members: ${guild.name} - q1 Network` },
  projects: { path: `/${guild.key}/projects`, title: `Projects: ${guild.name} - q1 Network` },
});

/*=== VIEW CLASS ===*/

export class GuildView extends TabRouteMixin(BaseGuildView) {
  static styles = [
    ...super.styles,
    guildViewStyles,
  ];

  static properties = {
    ...super.properties,

    organizers: { type: Array },
    firstActiveGuildMembers: { type: Array },
    firstActiveGuildProjects: { type: Array },
    _authorMembersByRef: { type: Object },
    _allMembersByRef: { type: Object },
    _allProjectsByRef: { type: Object },
    _cachedGuildMembers: { type: Array },
    _cachedGuildProjects: { type: Array },
    
    logItems: { type: Array },
    logItems_firstPageLoaded: { type: Boolean },
    logItems_nextPage: { type: Number },
    logItems_querySize: { type: Number },
    logItems_pageHistory: { type: Array },
    logItems_loading: { type: Boolean },

    groupedExternalResources: { type: Object },
  };

  constructor() {
    super();

    this.organizers = [];
    this.firstActiveGuildMembers = [];
    this.firstActiveGuildProjects = [];
    this._authorMembersByRef = {};
    this._allMembersByRef = {};
    this._allProjectsByRef = {};
    this._cachedGuildMembers = [];
    this._cachedGuildProjects = [];

    this.logItems = [];
    this.logItems_firstPageLoaded = false;
    this.logItems_nextPage = null;
    this.logItems_querySize = null;
    this.logItems_pageHistory = null;
    this.logItems_loading = false;

    this.groupedExternalResources = {};
  }

  /*=== LIFECYCLE ===*/

  async guildLoaded() {
    // Build list of caches to query, if any
    const cachesToGet = guildDetailViewCaches(this.guild?.id, this.isSignedIn, this.context);
    if (cachesToGet.length) {
      // Get the caches and wait for refresh if either are new or stale
      let {
        caches, anyAreNewOrStale, refreshesComplete,
      } = await this.appElement.cacheManager.getCaches(cachesToGet, CACHE_MAX_AGE);
      const [guildContentCache, allMembersSearchCache, allProjectsSearchCache] = anyAreNewOrStale
        ? await refreshesComplete
        : caches;

      const { browseExternalResources } = q1ExternalResourceFunctions;
    
      const response = await browseExternalResources({
        subjectId: this.guild.id,
        subjectCollection: 'guilds',
      });
    
      const allResources = response?.data?.externalResources || [];
      const externalResources = allResources.filter(resource => resource.status === 'active');
    
      const grouped = new Map();
      externalResources.forEach(resource => {
        const icon = resource.icon || 'link';
        if (!grouped.has(icon)) grouped.set(icon, []);
        grouped.get(icon).push(resource);
      });
    
      this.groupedExternalResources = grouped;        
      
      // Build the organizers list from the cache
      this.organizers = (guildContentCache?.body?.organizerMembers || []).map(organizer => {
        const enriched = this._allMembersByRef?.[organizer.ref] || organizer;
        const enrichedOrganizer = enrichMember(enriched);
        enrichedOrganizer.isPoint = this.guild.point === enrichedOrganizer.ref;
        enrichedOrganizer.chipLink = `/${this.guild.key}/@${enrichedOrganizer.number}`;
        return enrichedOrganizer;
      }).sort((a, b) => {
        if (a.isPoint && !b.isPoint) return -1;
        if (!a.isPoint && b.isPoint) return 1;
        
        return a.name.localeCompare(b.name);
      });

      // Build the first active guild members list from the cache (used for home tab mini list)
      this.firstActiveGuildMembers = (guildContentCache?.body?.firstActiveGuildMembers || []);
      this.firstActiveGuildMembers.forEach(guildMember => enrichMember(guildMember.member));

      // Build the first active guild projects list from the cache (used for home tab mini list)
      this.firstActiveGuildProjects = (guildContentCache?.body?.firstActiveGuildProjects || []);
      this.firstActiveGuildProjects.forEach(guildProject => enrichProject(guildProject.project));

      // Build a map of organizer members to provide to the log item enrichment method
      this._authorMembersByRef = Object.fromEntries(this.organizers.map(m => [m.ref, m]));

      if (this.context.isOrganizer) {
        // For organizers, query the log items right away
        // Why? (1) Because they create log items and need to see the latest
        //      (2) Because drafts/archives are not in the caches
        await this.refreshLogItems();

        // Also leave the cached guild members and cached guild projects NULL, 
        // since we'll live query that right away too
        this._cachedGuildMembers = null; // must be falsy to trigger a live query
        this._cachedGuildProjects = null; // must be falsy to trigger a live query
      } else {
        // For non-organizers, build the first page of log items from the cache
        this.logItems = (guildContentCache?.body?.logItems || [])
          .map(li => enrichLogItem(li, this._authorMembersByRef));
        this.logItems_firstPageLoaded = false;
        this.logItems_nextPage = null;

        // Also build the cached guild members list & guilds projects from the guild content cache
        // Note: Even an empty array will prevent a live query on load.
        this._cachedGuildMembers = (guildContentCache?.body?.firstActiveGuildMembers || [])
        .map(guildMember =>enrichMembership(guildMember, this._allMembersByRef));
        
        this._cachedGuildProjects = (guildContentCache?.body?.firstActiveGuildProjects || []);
      }

      // Build a map of all members to provide to the membership enrichment method
      if (allMembersSearchCache?.data)
        this._allMembersByRef = buildRefPathMap(allMembersSearchCache?.data, 'members', 'members');

      // Build a map of all projects to provide to the project enrichment method
      if (allProjectsSearchCache?.data)
        this._allProjectsByRef = buildRefPathMap(
          allProjectsSearchCache?.data, 
          'projects', 
          'projects'
        );
    }

    this.setViewMeta();

  }

  /*=== ACTIONS ===*/

  async refreshLogItems() {
    const { browseLogItems } = q1LogItemFunctions;

    this.logItems_loading = true;

    try {
      const response = await browseLogItems({
        subjectId: this.guild.id,
        subjectCollection: 'guilds',
        pageSize: LOG_ITEMS_PAGE_SIZE,
      });
      const { logItems, nextPage, querySize, pageHistory } = response.data;

      this.logItems = logItems.map(li => enrichLogItem(li, this._authorMembersByRef));
      this.logItems_firstPageLoaded = true;
      this.logItems_nextPage = nextPage;
      this.logItems_querySize = querySize;
      this.logItems_pageHistory = pageHistory;
    } catch (error) {
      console.error('Error loading log items', error);
      this.showToast(error.message, 'Error loading log items', 'danger');
    } finally {
      this.logItems_loading = false;
    }
  }

  async loadMoreLogItems() {
    const { browseLogItems } = q1LogItemFunctions;

    this.logItems_loading = true;

    try {
      const response = await browseLogItems({
        subjectId: this.guild.id,
        subjectCollection: 'guilds',
        pageSize: LOG_ITEMS_PAGE_SIZE,
        queryPage: this.logItems_nextPage || 1,
        pageHistory: this.logItems_pageHistory || [],
      });
      const { logItems, nextPage, pageHistory } = response.data;

      const enrichedLogItems = logItems.map(li => enrichLogItem(li, this._authorMembersByRef));
      this.logItems = this.logItems_firstPageLoaded
        ? [...this.logItems, ...enrichedLogItems]
        : enrichedLogItems;
      this.logItems_firstPageLoaded = true;
      this.logItems_nextPage = nextPage;
      this.logItems_pageHistory = pageHistory;
    } catch (error) {
      console.error('Error loading log items', error);
      this.showToast(error.message, 'Error loading log items', 'danger');
    } finally {
      this.logItems_loading = false;
    }
  }

  showActivationPanel() {
    const activationPanel = this.shadowRoot.getElementById('activation-panel');
    if (!this.canManageGuild || !activationPanel) return;
    activationPanel.show();
  }

  /*=== TAB ROUTE IMPLEMENTATION METHODS ===*/

  _getTabPath(tabKey) {
    if (!this.guild) return window.location.pathname;

    return TAB_SETTINGS(this.guild)[tabKey]?.path || TAB_SETTINGS(this.guild).home.path;
  }

  _getTabTitle(tabKey) {
    if (!this.guild) return 'Guild View - q1 Network';

    return TAB_SETTINGS(this.guild)[tabKey]?.title || 'Guild View - q1 Network';
  }

  /*=== HANDLERS ===*/

  handleMenuSelect(event) {
    event.preventDefault();
    const menuItemValue = event.detail?.item?.value;

    switch (menuItemValue) {
      case 'your-profile':
        this.appElement.navigateTo(this.currentGuildMemberProfilePath, event);
        break;

      case 'invite-members':
        this.appElement.navigateTo(`/${this.guild.key}/invite`, event);
        break;

      case 'join-page':
        this.appElement.navigateTo(`/${this.guild.key}/join`, event);
        break;
      
      case 'activation-panel':
        this.showActivationPanel();
        break;

      case 'manage-members':
        this.appElement.navigateTo(`/${this.guild.key}/manage`, event);
        break;

      case 'manage-details':
        this.appElement.navigateTo(`/${this.guild.key}/manage/details`, event);
        break;

      case 'manage-theme':
        this.appElement.navigateTo(`/${this.guild.key}/manage/theme`, event);
        break;

      case 'manage-settings':
        this.appElement.navigateTo(`/${this.guild.key}/manage/settings`, event);
        break;
      
      default:
        console.error(`INVALID menu select item value: ${menuItemValue}`);
    }
  }

  /*=== GETTERS ===*/

  get hasGuildProjects(){
    return this.currentMember?.flags?.includes('gp-testing') || false;
  }

  get showMoreLogItemsButton() {
    return (this.logItems_nextPage > 0) || !this.logItems_firstPageLoaded;
  }

  get canSeeMemberList() {
    return this.context?.visibleData?.includes?.('member-list') || false;
  }

  get canSeeProjectList() {
    return this.context?.visibleData?.includes?.('project-list') || false;
  }

  get memberCount() {
    const memberCount = this.guild?.memberCount || 1;
    const organizerCount = this.guild?.organizers?.length || 0;

    return Math.max(memberCount, organizerCount);
  }

  get projectCount() {
    return this.guild?.projectCount || 0;
  }

  get memberCountLabel() {
    if (this.memberCount === 0) return 'No Guild Members';
    if (this.memberCount === 1) return '1 Guild Member';
    return `${this.memberCount} Guild Members`;
  }

  get projectCountLabel() {
    if (this.projectCount === 0) return 'No Guild Projects';
    if (this.projectCount === 1) return '1 Guild Project';
    return `${this.projectCount} Guild Projects`;
  }

  get proposedMemberCount() {
    return this.guild?.membersPending?.length || 0;
  }

  get proposedMemberLabel() {
    if (this.proposedMemberCount === 0) return 'No proposed members';
    if (this.proposedMemberCount === 1) return '1 proposed member';
    return `${this.proposedMemberCount} proposed members`;
  }

  /** Returns the path of the current member's guild member profile (whether or not it exists). */
  get currentGuildMemberProfilePath() {
    if (!this.currentMember?.number || !this.guild?.detailViewPath) return '';

    return `${this.guild.detailViewPath}/@${this.currentMember.number}`;
  }

  get isActivationPanelAvailable() {
    return this.canManageGuild &&
      ['created', 'published', 'seeded', 'activated'].includes(this.guild?.activationStatus);
  }
  
  get isActivationBarOpen() {
    // Reduce visibility slightly when activated for detail view
    return this.canManageGuild &&
      ['created', 'published', 'seeded'].includes(this.guild?.activationStatus);
  }

  get metaContext() {
    return { guild: this.guild };
  }  

  /*=== TEMPLATES ===*/
  
  get viewModeTemplate_loaded() {
    return html`
      ${this.activationBarTemplate}
      
      ${this.loadedHeaderTemplate}

      <sl-tab-group
        id="guild-tabs"
        @sl-tab-show=${this.handleTabShow}
        data-tab-route="source"
        class="sl-themed"
      >
        <sl-tab
          slot="nav"
          panel="home"
        >Home</sl-tab>
        <sl-tab
          slot="nav"
          panel="about"
        >About</sl-tab>
        <sl-tab
          slot="nav"
          panel="members"
        >Members</sl-tab>
        ${!this.hasGuildProjects ? null : html`
        <sl-tab
          slot="nav"
          panel="projects"
        >Projects</sl-tab>
        `}
        
        <sl-divider class="full-width"></sl-divider>

        ${this.loadedBodyContentTemplate}
      </sl-tab-group>

      ${this.activationPanelTemplate}
    `;
  }

  get loadedHeaderTemplate() {
    return html`
      <guild-header>
        ${!this.guild.banner?.standard?.url ? null : html`
          <guild-banner>
            <img src=${this.guild.banner.standard.url}/>
          </guild-banner>
        `}

        <guild-overlay></guild-overlay>

        <guild-header-content>
          <sl-avatar
            id="guild-avatar"
            label="${this.guild.name}"
            image=${this.guild.avatar?.standard?.url}
          >
            <sl-icon slot="icon" library="material" name="hive"></sl-icon>
          </sl-avatar>
          <div class="guild-header-info">
            <h1>${this.guild.name}</h1>
            <ul>
              <li>
                <sl-tooltip content=${this.guild.visibility_description}>
                  <sl-icon library="material" name=${this.guild.visibility_icon}></sl-icon>
                </sl-tooltip>
                <span class="hide-until-bp2">${this.guild.visibility_label}</span>
              </li>
              <li>
                <sl-icon library="material" name="groups"></sl-icon>
                ${this.memberCount}
                <span class="hide-until-bp2">
                  ${this.memberCount === 1 ? 'member' : 'members'}
                </span>
              </li>
              <li>
                <sl-icon library="material" name="bigtop_updates"></sl-icon>
                ${this.guild.followerCount || 1} 
                <span class="hide-until-bp2">followers</span>
              </li>
            </ul>
          </div>
        </guild-header-content>
      </guild-header>

      <guild-subheader class="sl-themed">
        ${this.buttonBarTemplate}

        <div class="inner-content">          
          ${!this.guild.headline ? null : html`
            <div id="guild-headline">${this.guild.headline}</div>
          `}
        </div>
      </guild-subheader>
    `;
  }

  get activationBarTemplate() {
    if (!this.isActivationBarOpen) return null;

    return html`
      <q1-alert-bar
        id="activation-bar"
        message="Continue activating your guild..."
        @q1-click=${this.showActivationPanel}
        icon="brightness_alert"
        btn-label="Next Steps"
        btn-icon="arrow_forward"
        variant="light"
        class="sl-themed"
        dismissible
      ></q1-alert-bar>
    `;
  }

  get activationPanelTemplate() {
    if (!this.isActivationPanelAvailable) return null;

    return html`
      <q1-guild-activation-panel
        id="activation-panel"
        .guild=${this.guild}
        class="sl-themed"
      ></q1-guild-activation-panel>
    `;
  }

  get loadedBodyContentTemplate() {
    return html`
      <sl-tab-panel name="home">
        ${this.homeTabTemplate}
      </sl-tab-panel>
      <sl-tab-panel name="about">
        ${this.aboutTabTemplate}
      </sl-tab-panel>
      <sl-tab-panel name="members">
        ${this.membersTabTemplate}
      </sl-tab-panel>
      ${!this.hasGuildProjects ? null : html`
      <sl-tab-panel name="projects">
        ${this.projectsTabTemplate}
      </sl-tab-panel>
      `}
    `;
  }
  
  get homeTabTemplate() {
    return html`
      <column-wrapper class="left-weighted">
        <left-column>
          <header-bar class="nested">
            <h3>Guild Log</h3>

            ${!this.isSignedIn ? null : html`
              <button-bar>
                ${!this.context.canEdit ? null : html`
                  <sl-button
                    size="small"
                    href="/${this.guildKey}/post"
                    pill
                  >
                    <sl-icon slot="prefix" library="material" name="campaign"></sl-icon>
                    Post Update
                  </sl-button>
                `}
                
                <sl-button
                  size="small"
                  @click=${this.refreshLogItems}
                  pill
                >
                  ${this.logItems_loading ? html`
                    <sl-spinner slot="prefix"></sl-spinner>
                    Loading...
                  ` : html`
                    <sl-icon slot="prefix" library="material" name="refresh"></sl-icon>
                    Refresh Log Items
                  `}
                </sl-button>
              </button-bar>
            `}
          </header-bar>
          
          <div id="log-items-list">
            ${this.logItemsListTemplate}
          </div>
        </left-column>

        <right-column>
          <div class="column-item">
            <h3 class="with-border">Guild Organizers</h3>
  
            <div class="organizers">
              ${this.organizers.map(organizer => html`
                <q1-member-chip
                  .member=${organizer}
                  icon=${organizer.isPoint ? 'assistant_navigation' : ''}
                  icon-tooltip="Current Guild Point"
                  ?signed-in=${this.isSignedIn}
                  show-linkedin
                ></q1-member-chip>
              `)}
            </div>
          </div>
          
          <div class="column-item">
            <h3 class="with-border">${this.memberCountLabel}</h3>
  
            <avatar-stack>
              ${this.firstActiveGuildMembers.map(guildMember => html`
                <sl-avatar
                  image=${guildMember.member?.profile?.mini?.url}
                  shape="circle"
                ></sl-avatar>
              `)}
            </avatar-stack>

            <a href="/${this.guildKey}/members" id="member-mini-list-link">
              Browse all guild members
            </a>
          </div>

          ${!this.hasGuildProjects ? null : html`      
          <div class="column-item">
            <h3 class="with-border">${this.memberCountLabel}</h3>

            <avatar-stack>
              ${this.firstActiveGuildProjects.map(guildProject => html`
                <sl-avatar
                  image=${guildProject.project?.avatar?.mini?.url}
                  shape="rounded"
                ></sl-avatar>
              `)}
            </avatar-stack>

            <a href="/${this.guildKey}/projects" id="member-mini-list-link">
              Browse all guild projects
            </a>
          </div>
          `}
        </right-column>
      </column-wrapper>
    `;
  }

  get logItemsListTemplate() {
    if (!this.logItems?.length) return html`
      <empty-alert>
        <sl-icon library="material" name="block"></sl-icon>
        ${this.context?.canEdit ? html`
          You haven't posted any guild log updates yet!
        ` : html`
          There are no guild log updates visible to you yet.
        `}
      </empty-alert>
    `;

    return html`
      ${repeat(this.logItems, (logItem) => logItem.id, (logItem) => html`
        <q1-log-item-card .logItem=${logItem}></q1-log-item-card>
      `)}

      <button-bar class="center-align">
        ${!this.showMoreLogItemsButton ? html`
          <div class="no-more-pages">
            <span>
              <sl-icon library="material" name="task_alt"></sl-icon>
              You reached the end of the log!
            </span>
          </div>
        ` : html`
          <sl-button
            @click=${this.loadMoreLogItems}
            ?disabled=${this.logItems_loading}
            pill
          >
            ${this.logItems_loading ? html`
              <sl-spinner slot="prefix"></sl-spinner>
              Loading...
            ` : html`
              <sl-icon slot="prefix" library="material" name="more_horiz"></sl-icon>
              Load More
            `}
          </sl-button>
        `}
      </button-bar>
    `;
  }
  
  get aboutTabTemplate() {
    return html`
      <no-column-wrapper>
        <header-bar class="nested with-border">
          <h1>${this.guild.name}</h1>

          <button-bar>
            ${!this.context.canEdit ? '' : html`
              <sl-button
                href="/${this.guildKey}/manage/details"
                pill
              >
                <sl-icon slot="prefix" library="material" name="edit_note"></sl-icon>
                Edit Description
              </sl-button>
            `}
          </button-bar>
        </header-bar>
          
        ${!this.guild.description ? html`
          <empty-alert>
            <sl-icon library="material" name="block"></sl-icon>
            This guild has no description yet.

            ${!this.context.canEdit ? '' : html`
              <sl-button
                variant="primary"
                href="/${this.guildKey}/manage/details"
                pill
              >
                <sl-icon slot="prefix" library="material" name="post_add"></sl-icon>
                Add Description
              </sl-button>
            `}
          </empty-alert>
        ` : html`
          <q1-markdown .content=${this.guild.description}></q1-markdown>
        `}
      </no-column-wrapper>
    `;
  }
  
  get membersTabTemplate() {
    return html`
      <header-bar class="nested">
        <h2>Guild Members</h2>

        <button-bar class="align-center">
          ${!this.context.canEdit ? '' : html`
            ${!this.proposedMemberCount ? '' : html`
              <sl-tag variant="warning">${this.proposedMemberLabel}</sl-tag>
            `}

            <sl-button
              href="/${this.guildKey}/manage"
              pill
            >
              <sl-icon slot="prefix" library="material" name="manage_accounts"></sl-icon>
              Manage Members
            </sl-button>
          `}
        </button-bar>
      </header-bar>

      ${this.canSeeMemberList ? html`
        <q1-membership-list
          subject=${this.guild.ref}
          subject-detail-view-path=${this.guild.detailViewPath}
          query="guildMembers"
          .memberRefMap=${this._allMembersByRef}
          .memberships=${this._cachedGuildMembers}
          ?signed-in=${this.isSignedIn}
          ?enable-status-filter=${this.context.isMember}
          class="full-width"
        ></q1-membership-list>
      ` : html`
        <notice-panel>
          <div class="inner">
            <sl-icon library="material" name="visibility_off"></sl-icon>

            <h2>Not visible to you...</h2>

            <p>
              You do not have permissions to view the member list.
            </p>
          </div>
        </notice-panel>
      `}
    `;
  }

  get projectsTabTemplate() {
    return html`
      <header-bar class="nested">
        <h2>Guild Projects</h2>

        <button-bar class="align-center">
          ${!this.context.canEdit ? '' : html`

            <sl-button
              href="/${this.guildKey}/manage/projects"
              pill
            >
              <sl-icon slot="prefix" library="material" name="manage_accounts"></sl-icon>
              Manage Projects
            </sl-button>
          `}
        </button-bar>
      </header-bar>

      ${this.canSeeProjectList ? html`
        <q1-projects-list
          subject=${this.guild.ref}
          subject-detail-view-path=${this.guild.detailViewPath}
          query="guildProjects"
          .projectRefMap=${this._allProjectsByRef}
          .projects=${this._cachedGuildProjects}
          ?signed-in=${this.isSignedIn}
          ?enable-status-filter=${this.context.isMember}
          class="full-width"
        ></q1-projects-list>
      ` : html`
        <notice-panel>
          <div class="inner">
            <sl-icon library="material" name="visibility_off"></sl-icon>

            <h2>Not visible to you...</h2>

            <p>
              You do not have permissions to view the project list.
            </p>
          </div>
        </notice-panel>
      `}
    `;
  }

  get buttonBarTemplate() {
    if (this.context.isProposedMember) return html`
      <button-bar>
        ${this.buttonBarInfoTemplate}

        <sl-button
          variant="primary"
          href="/${this.guildKey}/@${this.currentMember.number}"
          pill
        >
          <sl-icon slot="prefix" library="material" name="rule"></sl-icon>
          Respond to Invite
        </sl-button>
      </button-bar>
    `;
    
    if (this.context.isMember) return html`
      <button-bar>
        ${this.buttonBarInfoTemplate}

        <sl-button
          variant="primary"
          href="/${this.guildKey}/invite"
          pill
        >
          <sl-icon slot="prefix" library="material" name="person_add"></sl-icon>
          Invite
        </sl-button>

        <sl-dropdown hoist>
          <sl-button slot="trigger" caret variant="text" class="menu-button">
            <sl-icon library="material" name="settings"></sl-icon>
          </sl-button>

          <sl-menu @sl-select=${this.handleMenuSelect}>
            <sl-menu-item value="your-profile">
              <sl-icon slot="prefix" library="material" name="assignment_ind"></sl-icon>
              Your Guild Profile
            </sl-menu-item>
            
            <sl-menu-item value="invite-members">
              <sl-icon slot="prefix" library="material" name="forward_to_inbox"></sl-icon>
              Invite New Members
            </sl-menu-item>

            <sl-menu-item value="join-page">
              <sl-icon slot="prefix" library="material" name="borg"></sl-icon>
              Guild Join Page
            </sl-menu-item>

            ${!this.canManageGuild ? '' : html`
              <sl-divider class="menu-divider"></sl-divider>

              ${!this.isActivationPanelAvailable ? '' : html`
                <sl-menu-item value="activation-panel">
                  <sl-icon slot="prefix" library="material" name="brightness_alert"></sl-icon>
                  <strong>
                    Guild Activation Flow
                  </strong>
                </sl-menu-item>
              `}

              <sl-menu-item value="manage-members">
                <sl-icon slot="prefix" library="material" name="manage_accounts"></sl-icon>
                Manage Members
              </sl-menu-item>

              <sl-menu-item value="manage-details">
                <sl-icon slot="prefix" library="material" name="notes"></sl-icon>
                Edit Guild Details
              </sl-menu-item>

              <sl-menu-item value="manage-theme">
                <sl-icon slot="prefix" library="material" name="palette"></sl-icon>
                Change Theme
              </sl-menu-item>

              ${!this.isGuildAdmin ? '' : html`
                <sl-divider class="menu-divider"></sl-divider>

                <sl-menu-item value="manage-settings">
                  <sl-icon slot="prefix" library="material" name="build"></sl-icon>
                  Guild Settings
                </sl-menu-item>
              `}
            `}
          </sl-menu>
        </sl-dropdown>
      </button-bar>
    `;

    return html`
      <button-bar>
        ${this.buttonBarInfoTemplate}

        <sl-button
          variant="primary"
          href="/${this.guildKey}/join"
          pill
        >
          <sl-icon slot="prefix" library="material" name="how_to_reg"></sl-icon>
          Join the Guild
        </sl-button>
      </button-bar>
    `;
  }

  get buttonBarInfoTooltip() {
    let returnText = this.guild?.activationStatus_description || '';

    if (this.canManageGuild) returnText += ` ${this.guild?.activationStatus_nextStep || ''}`;

    return returnText;
  }

  get buttonBarInfoTemplate() {
    return html`
      <span class="info">
        <sl-tooltip content=${this.buttonBarInfoTooltip}>
          <sl-icon library="material" name=${this.guild?.activationStatus_icon}></sl-icon>
          ${this.guild?.activationStatus_label} Guild
        </sl-tooltip>
        ${this.externalResourceIconsTemplate}
      </span>
      `;
  }
  
  get externalResourceIconsTemplate() {
    if (!this.groupedExternalResources?.size) return null;
    
    return html`
      <span>&bull;</span>
      ${repeat(Array.from(this.groupedExternalResources.entries()),
      ([iconKey, items]) => iconKey, ([iconKey, items]) => {
        const icondata = externalResourceIconMap[iconKey] || {};
        const label = icondata.label || iconKey;
        const iconName = icondata.icon;
        const iconLibrary = icondata.iconLibrary;
  
        if (!iconName || !iconLibrary) return null;
  
        if (items.length === 1) {
          const item = items[0];
          return html`
            <sl-tooltip content=${label}>
              <a 
                href=${item.externalData?.url} 
                target="_blank" 
                rel="noopener noreferrer" 
                class="external-icon-link"
              >
                <sl-icon 
                  library=${iconLibrary} 
                  name=${iconName} 
                  class="external-icon"
                ></sl-icon>
              </a>
            </sl-tooltip>
          `;
        } else {
          return html`
            <sl-tooltip content=${label}>
              <sl-dropdown hoist>
                <sl-button 
                  slot="trigger" 
                  variant="text" 
                  size="small" 
                  caret 
                  class="external-icon-button"
                >
                  <sl-icon 
                    library=${iconLibrary} 
                    name=${iconName} 
                    class="external-icon"
                  ></sl-icon>
                </sl-button>
                <sl-menu>
                  ${items.map(item => html`
                    <sl-menu-item 
                      @click=${() => window.open(
                        item.externalData?.url, 
                        '_blank', 
                        'noopener,noreferrer'
                      )}
                    >
                      <sl-icon slot="prefix" library=${iconLibrary} name=${iconName}></sl-icon>
                      ${item.name}
                    </sl-menu-item>
                  `)}
                </sl-menu>
              </sl-dropdown>
            </sl-tooltip>
          `;
        }
      })}
    `;
  }
}

customElements.define('guild-view', GuildView);
